import { useCallback, useState } from 'react';

import { ArticleModuleMediaCollapsibleMetadataBox } from '#components/Article/Modules/ArticleModuleMediaCollapsibleMetadataBox';
import { MediaVideo } from '#components/Media/MediaVideo';
import { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { getDurationFromMediaModule } from '#cutils/media/getDurationFromMediaModule';
import { getIsMediaOffline } from '#cutils/media/getIsMediaOffline';
import { logger } from '#utils/logging/logger';

import { ArticleModuleMedia } from './ArticleModuleMedia';
import { ARTICLE_MODULE_MEDIA_IMAGE_SIZES } from './ArticleModuleMediaSize';
import type { ArticleModuleVideoFragment } from './__generated__/ArticleModuleVideo.generated';

type Props = {
	publicationDate: string | null | undefined;
	videoModule: ArticleModuleVideoFragment;
};

export function ArticleModuleVideo({ publicationDate, videoModule }: Props) {
	const videoDuration = getDurationFromMediaModule(videoModule);
	const videoUrl = videoModule.video?.hls;
	const thumbnail = videoModule.thumbnail;
	const isMediaOffline = getIsMediaOffline(videoModule.meta);

	const [mediaPlaybackStatus, setMediaPlaybackStatus] = useState(MediaContextPlaybackStatus.STOPPED);

	const handlePlaybackStatusChange = useCallback((e: MediaContextPlaybackStatus) => {
		setMediaPlaybackStatus(e);
	}, []);

	if (!videoUrl) {
		logger.error(`Module is of type 'video', but no video url was found in '%s'.`, videoModule.rowId);

		return null;
	}

	if (!thumbnail) {
		logger.error(`No thumbnail for video with module id '%s' found.`, videoModule.rowId);

		return null;
	}

	const collapsibleMetadataBox = (
		<ArticleModuleMediaCollapsibleMetadataBox
			articleModule={videoModule}
			mediaPlaybackStatus={mediaPlaybackStatus}
			moduleType={isMediaOffline ? 'IMAGE' : videoModule.type}
		/>
	);
	const video = (
		<MediaVideo
			copyright={videoModule.copyright}
			duration={videoDuration}
			meta={videoModule.meta}
			publicationDate={publicationDate}
			thumbnailAltText={thumbnail.altText}
			thumbnailCopyright={thumbnail.copyright}
			thumbnailUrl={thumbnail.url}
			title={videoModule.title || undefined}
			url={videoUrl}
			onPlayStatusChange={handlePlaybackStatusChange}
			moduleId={videoModule.rowId}
			sizes={ARTICLE_MODULE_MEDIA_IMAGE_SIZES}
		/>
	);

	return <ArticleModuleMedia collapsibleMetadataBox={collapsibleMetadataBox} media={video} />;
}
